/**
 * Achievements (owner, Sep 28: "we need to build an achievement system and make it configurable in the admin panel (a
 * counter for completing things, settings for: how many needed, what item (forever item, cannot be dropped and does not
 * contribute to backpack weight)/badge/mobile game or app one gets from the achievement)"). docs/DESIGN_ACHIEVEMENTS.md.
 *
 * Counters: `count(game, st, key, n)` is called where a thing is completed — a job of a kind, a police call, a kilometre
 * driven, a tree felled, a round at the bag … — and adds to the player's lifetime counters (`st.stats`, saved with the
 * character and already sent to its client as the sticky `st`). The keys are listed in COUNTERS; `jobs_<kind>` and
 * `visit_<place>` exist for every job kind and place. Achievements are content (content/achievements), overridden per world
 * from the admin panel (server/achievements.ts): an achievement watches one counter and is earned once, the moment it
 * reaches `need` — its reward is a forever item (an item with `forever: true`: never dropped, sold, stolen or lost, and
 * weightless), a badge, an unlock and/or cash. The earned ones are `st.achieved` (id → when, ms), saved and sent (`ac`).
 *
 * Unlocks: what an achievement can open on the phone. A player's unlocks are ids like `game:siren_shooter` (a phone game in
 * the Arcade) or `app:<id>` (a phone app), kept in `PlayerState.unlocks`, saved with the character and sent to its own client
 * (the sticky `ul`). `hasUnlock(id)` on Game and on NetClient answers for the local player — the Arcade shows the games whose
 * unlock the player has.
 */
import type { Game, PlayerState } from './game';
import type { Achievement } from '../content/types';
import type { Registry } from '../content/registry';

/** the unlocks the game knows (the admin panel's picker; an achievement may still name any `game:` / `app:` id) */
export const UNLOCKS: { id: string; name: string; icon: string }[] = [
  { id: 'game:siren_shooter', name: 'Siren Shooter (phone game)', icon: '🚨' },
  { id: 'game:delivery_dash', name: 'Delivery Dash (phone game)', icon: '📦' },
  { id: 'game:gull_rush', name: 'Gull Rush (phone game)', icon: '🐦' },
  { id: 'game:bag_beat', name: 'Bag Beat (phone game)', icon: '🥊' },
];
/** an unlock id's shape: `game:` or `app:` and a snake_case id */
export const UNLOCK_ID = /^(game|app):[a-z0-9_]{1,40}$/;

/** give a player an unlock (once); true when it is new. The achievements grant them; tests call it directly. */
export function grantUnlock(st: Pick<PlayerState, 'unlocks'>, id: string): boolean {
  if (!UNLOCK_ID.test(id) || st.unlocks.includes(id)) return false;
  st.unlocks.push(id);
  return true;
}
/** a saved list of unlocks, cleaned: known shape only, once each */
export function readUnlocks(v: unknown): string[] {
  return Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && UNLOCK_ID.test(x)))].slice(0, 200) : [];
}

/**
 * The counters the game keeps (the admin panel's picker and the lint's list). Most count one a time; the `km_` ones count
 * whole kilometres (the metres in between are kept aside, unsaved — so the sticky data does not change every step).
 * `jobs_<kind>` (delivery, taxi, bounty, race, raid, heist, escort, service, route, work, crew) and `visit_<place id>` exist
 * for every kind and place: the panel lists them from the content.
 */
export const COUNTERS: { key: string; name: string }[] = [
  { key: 'jobs', name: 'jobs finished (any kind)' },
  { key: 'earned', name: 'dollars earned from work' },
  { key: 'police', name: 'police calls closed on the shift' },
  { key: 'police_speeder', name: 'police calls: speeders stopped' },
  { key: 'police_chase', name: 'police calls: chases ended' },
  { key: 'police_burglar', name: 'police calls: burglars caught' },
  { key: 'police_crash', name: 'police calls: crashes cleared' },
  { key: 'arrests', name: 'burglars cuffed on the shift' },
  { key: 'races', name: 'street races won' },
  { key: 'holdups', name: 'stores held up' },
  { key: 'kills', name: 'kills' },
  { key: 'deaths', name: 'deaths' },
  { key: 'busted', name: 'times busted by the police' },
  { key: 'km_driven', name: 'km driven (at the wheel, on land)' },
  { key: 'km_flown', name: 'km flown (as the pilot)' },
  { key: 'km_sailed', name: 'km sailed (at a boat’s wheel)' },
  { key: 'km_walked', name: 'km on foot' },
  { key: 'trees', name: 'trees felled' },
  { key: 'workouts', name: 'gym workouts finished (any)' },
  { key: 'bag_rounds', name: 'rounds at the punching bag' },
  { key: 'sauna', name: 'sauna sessions' },
  { key: 'tv_minutes', name: 'minutes of TV watched' },
  { key: 'animals_fed', name: 'cats and dogs fed' },
  { key: 'animals_petted', name: 'cats and dogs petted' },
  { key: 'houses', name: 'houses bought' },
  { key: 'rented', name: 'places rented' },
  { key: 'towns', name: 'towns entered' },
  { key: 'days', name: 'midnights lived through in the city' },
  { key: 'bags', name: 'garbage bags loaded' },
  { key: 'grown', name: 'city growth work units' },
  { key: 'turf', name: 'turf taken' },
  { key: 'shows', name: 'radio shows hosted' },
  { key: 'fines', name: 'dollars of traffic fines' },
  { key: 'dives', name: 'dives under water' },
];
/** every counter key the game can count: the list, a `jobs_` for every job kind, a `visit_` for every place */
export function counterKeys(reg: Registry): { key: string; name: string }[] {
  const kinds = [...new Set(reg.all('job').map((j) => j.kind))].sort();
  return [...COUNTERS, ...kinds.map((k) => ({ key: `jobs_${k}`, name: `jobs finished: ${k}` })), ...reg.all('place').map((p) => ({ key: `visit_${p.id}`, name: `visits to ${p.name}` }))];
}

/** a counter's value for a player (kills and deaths live on the state itself) */
export function counterOf(st: Pick<PlayerState, 'stats' | 'kills' | 'deaths'>, key: string): number {
  return key === 'kills' ? st.kills : key === 'deaths' ? st.deaths : st.stats[key] ?? 0;
}

/** the achievements that are on, by the counter they watch — Game keeps one (setAchievements) */
export function indexAchievements(list: Achievement[]): Map<string, Achievement[]> {
  const m = new Map<string, Achievement[]>();
  for (const a of list) if (!a.off) { const l = m.get(a.counter); if (l) l.push(a); else m.set(a.counter, [a]); }
  return m;
}
/** the list in the app's order */
export const byOrder = (a: Achievement, b: Achievement): number => (a.order ?? 100) - (b.order ?? 100) || a.name.localeCompare(b.name);

/**
 * Count a thing done: `n` more on the counter `key`, and any achievement on it that is now reached is earned. Cheap: an
 * addition and a map lookup, and a loop over the (few) achievements on that one key.
 */
export function count(game: Game, st: PlayerState, key: string, n = 1): void {
  if (!(n > 0)) return;
  if (key === 'kills') st.kills += n; else if (key === 'deaths') st.deaths += n; else st.stats[key] = (st.stats[key] ?? 0) + n;
  check(game, st, key);
}
/** the achievements on one counter (or all of them) that the player has reached and not yet earned: earned now, one toast */
export function check(game: Game, st: PlayerState, key?: string): Achievement[] {
  const list = key === undefined ? game.achievementList().filter((a) => !a.off) : game.achievementsOn(key);
  const got: Achievement[] = [];
  for (const a of list) if (!st.achieved[a.id] && counterOf(st, a.counter) >= a.need) { earn(game, st, a); got.push(a); }
  if (got.length) announce(game, st, got);
  return got;
}
/** earned: the date, the reward (a forever item once, the unlock, the cash; the badge is the achievement's own) */
export function earn(game: Game, st: PlayerState, a: Achievement): void {
  st.achieved[a.id] = Date.now();
  const r = a.reward, p = st.person;
  if (r?.item && game.reg.get('item', r.item)) p.items[r.item] = Math.max(1, p.items[r.item] ?? 0);
  if (r?.unlock) grantUnlock(st, r.unlock);
  if (r?.cash) p.items.cash = (p.items.cash ?? 0) + Math.round(r.cash);
}
/** what an achievement gives, in words (the toast, the app) */
export function rewardText(reg: Registry, a: Achievement): string {
  const r = a.reward; if (!r) return '';
  const parts: string[] = [];
  if (r.item) parts.push(reg.get('item', r.item)?.name ?? r.item);
  if (r.badge) parts.push(`the ${r.badge} badge`);
  if (r.unlock) parts.push(UNLOCKS.find((u) => u.id === r.unlock)?.name ?? r.unlock);
  if (r.cash) parts.push(`$${r.cash}`);
  return parts.join(', ');
}
/** the toast: one line an achievement (three at most, then a count) and the fanfare for its player alone */
function announce(game: Game, st: PlayerState, got: Achievement[]): void {
  const p = st.person;
  for (const a of got.slice(0, 3)) { const rw = rewardText(game.reg, a); game.say(`🏆 Achievement: ${a.icon} ${a.name}${rw ? ` — ${rw}` : ''}`, p); }
  if (got.length > 3) game.say(`🏆 …and ${got.length - 3} more — see the Achievements app`, p);
  game.events.push({ kind: 'achievement', x: p.x, y: p.y, who: p.id });
}
/** a saved `achieved`: known shape only (id → ms) */
export function readAchieved(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (v && typeof v === 'object') for (const [k, t] of Object.entries(v as Record<string, unknown>)) if (/^[a-z][a-z0-9_]{0,63}$/.test(k) && typeof t === 'number' && Number.isFinite(t)) out[k] = t;
  return out;
}
/** the badges a player wears: from the achievements earned (newest first) that give one — its title and the achievement's icon */
export function badgesOf(list: Achievement[], achieved: Record<string, number>): { name: string; icon: string; at: number }[] {
  return list.filter((a) => a.reward?.badge && achieved[a.id]).map((a) => ({ name: a.reward!.badge!, icon: a.icon, at: achieved[a.id] })).sort((a, b) => b.at - a.at);
}

/** an item that is a trophy: never dropped, sold, stashed, stolen or lost, and weightless in the backpack */
export function isForever(reg: Registry, id: string): boolean { return !!reg.get('item', id)?.forever; }

/**
 * The distance counters: metres gone this step by the player — at a vehicle's wheel (driven / flown / sailed by its class) or
 * on foot — kept aside and counted a whole kilometre at a time. `moved` is the step's distance in metres.
 */
export function countDistance(game: Game, st: PlayerState, key: 'km_driven' | 'km_flown' | 'km_sailed' | 'km_walked', moved: number): void {
  if (!(moved > 0) || moved > 200) return; // (a teleport, a respawn: not a trip)
  const acc = (st.metres ??= {}); const m = (acc[key] ?? 0) + moved;
  if (m >= 1000) { acc[key] = m % 1000; count(game, st, key, Math.floor(m / 1000)); } else acc[key] = m;
}

/** a world's changes to the content's achievements (the admin panel, server/achievements.ts): per content id a patch, and the world's own */
export interface AchievementOverrides { edits: Record<string, Partial<Achievement>>; added: Achievement[] }
/** the list a world plays with: the content's, each with the world's patch over it, then the world's own */
export function effectiveAchievements(reg: Registry, o?: AchievementOverrides): Achievement[] {
  const base = reg.all('achievement').map((a) => ({ ...a, ...(o?.edits[a.id] ?? {}), id: a.id }));
  return [...base, ...(o?.added ?? []).filter((a) => !reg.get('achievement', a.id))].sort(byOrder);
}
